import PACUv2 from '@/components/pages/kiosk/PACUv2/PACUv2';
import React, { Fragment, useState, useMemo, useEffect, useRef } from 'react';
import { getPatient } from './pacuAndPrep/mapPatients';
import { PatientScreen } from './pacuAndPrep/PatientScreen';
import PatientList from './pacuAndPrep/PatientList';
import {
  getBeds,
  getLogEntries,
  isPreOpAsPacu,
  isReadyForFamilyPreOp as isReadyForFamilyPreOpFn,
  isReadyForPickupPACU as isReadyForPickupPACUFn,
  isReadyForPickupPOSTOP as isReadyForPickupPOSTOPFn,
  getEventFlags,
} from './tablet/utils';
import ClientUpdater from '../../ClientUpdater';
import get from 'lodash/get';
import styled from 'styled-components';
import readyToPickup from '../../../assets/sound/readyToPickup.mp3';
import { getNestedValue } from '@/se/utilities/data/object';
import { PACU, POST_OP, PRE_OP } from '../../entities/room/enums';
import { playSound } from '@/components/pages/kiosk/monitor/hooks/useAlerts';
import { ScreenConfigurationProvider } from '@/contexts/ScreenConfigurationContext';
import { withScope } from '@/contexts/ScopeContext';
import type { RoomTabletProps } from './types/RoomTabletTypes';
import { useRoomTabletData } from './hooks/useRoomTabletData';
import { usePatientMutations } from './hooks/usePatientMutations';
import { CenteredSpinner } from '@/se/components/Spinner';
import { useToaster } from '@/components/core/Toaster';
import { getExistingDelayReason } from './pacuAndPrep/delayReasonUtils';
import type { TimeReasonSubmitValue } from './pacuAndPrep/types/PatientScreenTypes';

// Needed to solve safari overflow issues
const Wrap = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

function RoomTabletApp({
  roomType,
  roomId: roomIdProp,
  match,
  scope,
  configuration,
  hospitalOverviewMode,
}: RoomTabletProps) {
  const [selectedPatient, setSelectedPatient] = useState<number | null>(null);
  const readyToPickupCountRef = useRef(0);

  const isHospitalOverviewMode = Boolean(hospitalOverviewMode);

  // Use direct roomId prop if provided, otherwise fall back to match.params.roomId
  const roomIdFromMatch = match?.params?.roomId ? Number(match.params.roomId) : undefined;
  const roomId = isHospitalOverviewMode ? undefined : (roomIdProp ?? roomIdFromMatch);

  // Determine if we should filter patients to a specific room:
  // - Kiosk screens (have match.params.roomId) → filter to specific room
  // - Overview screens (roomId passed as prop, no match.params.roomId) → show ALL patients of roomType
  // - Hospital overview mode → show ALL patients
  const isKioskScreen = Boolean(roomIdFromMatch);
  const filterByRoom = isKioskScreen && !isHospitalOverviewMode;

  // Fetch all data using combined hook
  const { patients, room, patientPickupMessage, readyForFamilyMessage, screenConfiguration, loading } =
    useRoomTabletData(roomType, roomId, filterByRoom);

  // Get all mutations
  const mutations = usePatientMutations();
  const toaster = useToaster();

  // Filter patients by type if configured
  const showPatientTypes = configuration?.showPatientTypes;
  const filteredPatients = useMemo(() => {
    if (showPatientTypes && showPatientTypes.length > 0) {
      return patients.filter(patient => showPatientTypes.includes(patient.type));
    }
    return patients;
  }, [patients, showPatientTypes]);

  // Check for ready to pickup alert
  useEffect(() => {
    if (loading) return;

    const newReadyToPickupCount = filteredPatients.filter(_ => !!_.caretakerMessages).length;

    if (newReadyToPickupCount > readyToPickupCountRef.current) {
      if (roomType === PACU || roomType === POST_OP) {
        playSound(readyToPickup, getNestedValue('screenConfiguration.readyToPickupSound', { screenConfiguration }));
      }
    }

    readyToPickupCountRef.current = newReadyToPickupCount;
  }, [filteredPatients, loading, roomType, screenConfiguration]);

  // Mutation handlers
  const assignBedToSelectedPatient = async (bedNumber: string) => {
    if (selectedPatient) {
      await mutations.assignBed({
        variables: {
          id: selectedPatient,
          bed: `${bedNumber}`,
        },
      });
    }
  };

  const assignPriorityToSelectedPatient = async (priority: string) => {
    if (selectedPatient) {
      await mutations.assignPriority({
        variables: {
          id: selectedPatient,
          priority: `${priority}`,
        },
      });
    }
  };

  const markAsReady = async () => {
    let fn;
    if (roomType === PRE_OP) {
      fn = mutations.setPatientReadyInPREP;
    } else if (roomType === PACU) {
      fn = mutations.setPatientReadyInPACU;
    } else if (roomType === POST_OP) {
      fn = mutations.setPatientReadyInPOSTOP;
    }
    if (fn && selectedPatient) {
      await fn({
        variables: {
          id: selectedPatient,
        },
      });
    }
  };

  const markFamilyReady = async () => {
    let fn;
    if (roomType === PACU) {
      fn = mutations.setFamilyReadyForPickupPACU;
    } else if (roomType === POST_OP) {
      fn = mutations.setFamilyReadyForPickupPOSTOP;
    }
    if (fn && selectedPatient) {
      await fn({
        variables: {
          id: selectedPatient,
        },
      });
    }
  };

  // All mutation handlers expected by PatientScreen
  const onHoldProcedure = async () => {
    if (selectedPatient) {
      await mutations.setHoldProcedure({ variables: { id: selectedPatient } });
    }
  };

  const onUndoHoldProcedure = async () => {
    if (selectedPatient) {
      await mutations.setUndoHoldProcedure({ variables: { id: selectedPatient } });
    }
  };

  const onBlockNerve = async () => {
    if (selectedPatient) {
      await mutations.setBlockNerve({ variables: { id: selectedPatient } });
    }
  };

  const onUndoBlockNerve = async () => {
    if (selectedPatient) {
      await mutations.setUndoBlockNerve({ variables: { id: selectedPatient } });
    }
  };

  const onMarkAsReadyToSeeFamilyPreOp = async () => {
    if (selectedPatient) {
      await mutations.setReadyToSeeFamilyPreOp({ variables: { id: selectedPatient } });
    }
  };

  const onMarkAsNotReadyToSeeFamilyPreOp = async () => {
    if (selectedPatient) {
      await mutations.setNotReadyToSeeFamilyPreOp({ variables: { id: selectedPatient } });
    }
  };

  const onMarkAsReady = async () => {
    await markAsReady();
  };

  const onMarkAsNotReady = async () => {
    let fn;
    if (roomType === PRE_OP) {
      fn = mutations.setPatientNotReadyInPREP;
    } else if (roomType === PACU) {
      fn = mutations.setPatientNotReadyInPACU;
    } else if (roomType === POST_OP) {
      fn = mutations.setPatientNotReadyInPOSTOP;
    }
    if (fn && selectedPatient) {
      await fn({ variables: { id: selectedPatient } });
    }
  };

  const onMarkAsReadyForAnesthesia = async () => {
    if (selectedPatient) {
      await mutations.setReadyForAnesthesia({ variables: { id: selectedPatient } });
    }
  };

  const onMarkAsNotReadyForAnesthesia = async () => {
    if (selectedPatient) {
      await mutations.setNotReadyForAnesthesia({ variables: { id: selectedPatient } });
    }
  };
  const onMarkAsSpinalTimeout = async () => {
    if (selectedPatient) {
      await mutations.setSpinalTimeout({ variables: { id: selectedPatient } });
    }
  };

  const onMarkAsUndoSpinalTimeout = async () => {
    if (selectedPatient) {
      await mutations.setUndoSpinalTimeout({ variables: { id: selectedPatient } });
    }
  };

  const onMarkAsReadyForSurgeon = async () => {
    if (selectedPatient) {
      await mutations.setPatientReadyForSurgeon({ variables: { id: selectedPatient } });
    }
  };

  const onMarkAsNotReadyForSurgeon = async () => {
    if (selectedPatient) {
      await mutations.setPatientNotReadyForSurgeon({ variables: { id: selectedPatient } });
    }
  };

  const onMarkFamilyReady = async () => {
    await markFamilyReady();
  };

  const executeDischargeMutation = async (execute: () => Promise<unknown>) => {
    try {
      await execute();
    } catch (error: any) {
      if (error?.graphQLErrors?.some((graphQLError: any) => graphQLError.message === 'RetainedDischargeConflict')) {
        toaster.error(
          'The patient is no longer eligible to keep this beacon. Review the schedule, then choose Standard discharge or cancel.'
        );
      } else {
        toaster.error('Discharge failed. Please try again.');
      }
      throw error;
    }
  };

  const onDischarge = async (keepBeacon = false) => {
    if (selectedPatient) {
      await executeDischargeMutation(() => mutations.discharge({ variables: { id: selectedPatient, keepBeacon } }));
    }
  };

  const onDischargePhysicianLastPatient = async (keepBeacon = false) => {
    if (selectedPatient) {
      await executeDischargeMutation(() =>
        mutations.dischargePhysicianLastPatient({ variables: { id: selectedPatient, keepBeacon } })
      );
    }
  };

  const onDischargeAnesthesiologistLastPatient = async (keepBeacon = false) => {
    if (selectedPatient) {
      await executeDischargeMutation(() =>
        mutations.dischargeAnesthesiologistLastPatient({ variables: { id: selectedPatient, keepBeacon } })
      );
    }
  };

  const onDischargeBothLastPatient = async (keepBeacon = false) => {
    if (selectedPatient) {
      await executeDischargeMutation(() =>
        mutations.dischargeBothLastPatient({ variables: { id: selectedPatient, keepBeacon } })
      );
    }
  };

  const onTransferToChair = async () => {
    if (selectedPatient) {
      await mutations.transferToChair({ variables: { id: selectedPatient } });
    }
  };

  const onTransferToPT = async () => {
    if (selectedPatient) {
      await mutations.transferToPT({ variables: { id: selectedPatient } });
    }
  };

  const onPTComplete = async () => {
    if (selectedPatient) {
      await mutations.ptComplete({ variables: { id: selectedPatient } });
    }
  };

  const onMoveToOr = async (roomId: number) => {
    if (selectedPatient) {
      const result = await mutations.moveToOr({ variables: { id: selectedPatient, roomId } });
      if (result?.data?.setRoomForPatient === false) {
        toaster.warning('Room change is already in progress for this patient. Please try again.');
        throw new Error('Room change conflict');
      }
    }
  };

  const onMoveToPostOp = async (roomId: number) => {
    if (selectedPatient) {
      const result = await mutations.setRoom({ variables: { id: selectedPatient, roomId } });
      if (result?.data?.setRoomForPatient === false) {
        toaster.warning('Room change is already in progress for this patient. Please try again.');
        throw new Error('Room change conflict');
      }
    }
  };

  const onAssignBed = async (bedNumber: string) => {
    await assignBedToSelectedPatient(bedNumber);
  };

  const onDischargePatient = async (keepBeacon = false) => {
    await onDischarge(keepBeacon);
  };

  const onCancelVisit = async () => {
    if (selectedPatient) {
      // Cancel visit is typically a discharge or void operation
      await mutations.discharge({ variables: { id: selectedPatient } });
    }
  };

  // Orthopedic time edit handlers
  const onTransferToChairTimeSubmit = async (value: TimeReasonSubmitValue) => {
    if (!selectedPatient) return;

    const patient = getPatient(filteredPatients, selectedPatient);
    const existing = patient?.events?.transferredToChairAt;
    const convertToDateTime = (timestamp: Date, time: string) => {
      const dateTime = new Date(timestamp);
      const [hours, minutes] = time.split(':').map(e => parseInt(e, 10));
      dateTime.setHours(hours);
      dateTime.setMinutes(minutes);
      return dateTime;
    };

    // Save the timestamp only when the time actually changed.
    if (value.time) {
      const newDateTime = convertToDateTime(existing || new Date(), value.time);
      const timeChanged = !existing || newDateTime.toISOString() !== new Date(existing).toISOString();
      if (timeChanged) {
        await mutations.setPatientEventTimestamp({
          variables: {
            patientId: selectedPatient,
            eventType: JSON.stringify({
              __type: 'TransferredToChair',
              manualEntryBy: null,
              manualTimestamp: null,
            }),
            timestamp: newDateTime,
          },
          refetchQueries: ['listPacuPrepPostopPatients'],
        });
      }
    }

    // Independently save the delay reason only when it differs from the current value.
    if (value.reason !== undefined) {
      const currentReason = getExistingDelayReason(patient, 'oob') || null;
      const nextReason = value.reason || null;
      if (nextReason !== currentReason && mutations.editPatientOobWarningDelayReason) {
        await mutations.editPatientOobWarningDelayReason({
          variables: { id: selectedPatient, oobWarningDelayReason: nextReason },
        });
      }
    }
  };

  const onTransferToPTTimeSubmit = async (value: TimeReasonSubmitValue) => {
    if (!selectedPatient) return;

    const patient = getPatient(filteredPatients, selectedPatient);
    const existing = patient?.events?.transferredToPTAt;
    const convertToDateTime = (timestamp: Date, time: string) => {
      const dateTime = new Date(timestamp);
      const [hours, minutes] = time.split(':').map(e => parseInt(e, 10));
      dateTime.setHours(hours);
      dateTime.setMinutes(minutes);
      return dateTime;
    };

    // Save the timestamp only when the time actually changed.
    if (value.time) {
      const newDateTime = convertToDateTime(existing || new Date(), value.time);
      const timeChanged = !existing || newDateTime.toISOString() !== new Date(existing).toISOString();
      if (timeChanged) {
        await mutations.setPatientEventTimestamp({
          variables: {
            patientId: selectedPatient,
            eventType: JSON.stringify({
              __type: 'TransferredToPT',
              manualEntryBy: null,
              manualTimestamp: null,
            }),
            timestamp: newDateTime,
          },
          refetchQueries: ['listPacuPrepPostopPatients'],
        });
      }
    }

    // Independently save the delay reason only when it differs from the current value.
    if (value.reason !== undefined) {
      const currentReason = getExistingDelayReason(patient, 'pt') || null;
      const nextReason = value.reason || null;
      if (nextReason !== currentReason && mutations.editPatientPtWarningDelayReason) {
        await mutations.editPatientPtWarningDelayReason({
          variables: { id: selectedPatient, ptWarningDelayReason: nextReason },
        });
      }
    }
  };

  const onPTCompleteTimeSubmit = async (value: TimeReasonSubmitValue) => {
    if (!selectedPatient) return;

    const patient = getPatient(filteredPatients, selectedPatient);
    const existing = patient?.events?.ptCompleteAt;
    const convertToDateTime = (timestamp: Date, time: string) => {
      const dateTime = new Date(timestamp);
      const [hours, minutes] = time.split(':').map(e => parseInt(e, 10));
      dateTime.setHours(hours);
      dateTime.setMinutes(minutes);
      return dateTime;
    };

    // Save the timestamp only when the time actually changed.
    if (value.time) {
      const newDateTime = convertToDateTime(existing || new Date(), value.time);
      const timeChanged = !existing || newDateTime.toISOString() !== new Date(existing).toISOString();
      if (timeChanged) {
        await mutations.setPatientEventTimestamp({
          variables: {
            patientId: selectedPatient,
            eventType: JSON.stringify({
              __type: 'PTComplete',
              manualEntryBy: null,
              manualTimestamp: null,
            }),
            timestamp: newDateTime,
          },
          refetchQueries: ['listPacuPrepPostopPatients'],
        });
      }
    }

    // Independently save the delay reason only when it differs from the current value.
    if (value.reason !== undefined) {
      const currentReason = getExistingDelayReason(patient, 'ptComplete') || null;
      const nextReason = value.reason || null;
      if (nextReason !== currentReason && mutations.editPatientPtCompleteWarningDelayReason) {
        await mutations.editPatientPtCompleteWarningDelayReason({
          variables: { id: selectedPatient, ptCompleteWarningDelayReason: nextReason },
        });
      }
    }
  };

  const goBack = () => setSelectedPatient(null);

  // Loading state
  if (loading || (!isHospitalOverviewMode && !room)) {
    return <CenteredSpinner size={'5rem'} />;
  }

  // Module flags
  const hasNoteModule = scope?.hospital?.modules?.noteTablet || false;
  const hasProcedureNoteModule = scope?.hospital?.modules?.procedureNoteTablet || false;
  const hasPatientCharting = scope?.hospital?.modules?.patientCharting || false;

  const isPreOp = roomType === PRE_OP;
  const isPacu = roomType === PACU;
  const isPostOp = roomType === POST_OP;

  const hasPreOpPriorityModule = isPreOp ? scope?.hospital?.modules?.preOpPriority?.hasPreOpPriority || false : false;
  const priorityCounter = isPreOp ? scope?.hospital?.modules?.preOpPriority?.priorityCounter || 0 : 0;

  const usePACUv2 = configuration?.screenVersion === 1 && !isHospitalOverviewMode;

  // Render selected patient screen
  if (selectedPatient) {
    const patient = getPatient(filteredPatients, selectedPatient);
    if (!patient) {
      setSelectedPatient(null);
      return null;
    }

    if (!room) {
      console.error('[RoomTabletApp] Missing room data when rendering PatientScreen');
      return <div>Room data unavailable</div>;
    }

    const patientLogs = getLogEntries(patient);
    const events = (patient as any).events || {};
    const eventFlags = getEventFlags(events, roomType);

    const isReadyForPickupPACU = isReadyForPickupPACUFn(patientLogs);
    const isReadyForPickupPOSTOP = isReadyForPickupPOSTOPFn(patientLogs);

    const isPatientReady = eventFlags.isReady;

    const isReadyToSeeFamilyPreOp = isReadyForFamilyPreOpFn(patientLogs)(roomType);
    const isReadyForAnesthesia = isPreOp && eventFlags.isReadyForAnesthesia;
    const isReadyForSurgeon = isPreOp && eventFlags.isReadyForSurgeon;
    const isHoldProcedure = isPreOp && eventFlags.isHoldProcedure;
    const isBlockNerve = (isPreOp || isPostOp || isPacu) && eventFlags.isBlockNerveActive;
    const isSpinalTimeout = isPreOp && eventFlags.isSpinalTimeout;

    const hasCaretakerConsent = get(patient, 'caretakerConsent', false);

    return (
      <Wrap style={{ display: 'block' }}>
        <ScreenConfigurationProvider value={screenConfiguration || null}>
          <PatientScreen
            patientPickupMessage={patientPickupMessage}
            readyForFamilyMessage={readyForFamilyMessage}
            headerData={{ goBack, patient }}
            patientId={selectedPatient}
            roomType={roomType}
            room={room}
            bodyData={{
              hasNoteModule,
              hasProcedureNoteModule,
              hasPreOpPriorityModule,
              hasPatientCharting,
              retainedBeaconInpatientEnabled: scope?.hospital?.modules?.retainedBeaconInpatient?.enabled === true,
              priorityCounter,
              assignBed: true,
              markAsFamilyReady: true,
              markAsFamilyReadyPreOp: isPreOpAsPacu(roomType, patientLogs),
              markAsReadyForAnesthesia: isPreOp,
              markAsReadyForSurgeon: isPreOp,
              markAsSpinalTimeout: isPreOp,
              markAsUndoSpinalTimeout: isPreOp,
              dischargePatient: true,
              moveToOR: isPreOp,
              cancelVisit: isPreOp,
              holdProcedure: isPreOp,
              blockNerve: isPreOp || isPacu || isPostOp,
              isPatientReady,
              isReadyToSeeFamilyPreOp,
              isFamilyReady: isPatientReady, // Check if patient is ready to see family (BecameReadyInPACU/POSTOP)
              isReadyForPickupPACU,
              isReadyForPickupPOSTOP,
              isReadyForAnesthesia,
              isReadyForSurgeon,
              isSpinalTimeout,
              isHoldProcedure,
              isBlockNerve,
              hasCaretakerConsent,
              assignBedToSelectedPatient,
              assignPriorityToSelectedPatient,
              // Handler functions with 'on' prefix expected by PatientScreen
              onMarkAsReady,
              onMarkAsNotReady,
              onMarkFamilyReady,
              onMarkAsReadyForAnesthesia,
              onMarkAsNotReadyForAnesthesia,
              onMarkAsSpinalTimeout,
              onMarkAsUndoSpinalTimeout,
              onMarkAsReadyForSurgeon,
              onMarkAsNotReadyForSurgeon,
              onHoldProcedure,
              onUndoHoldProcedure,
              onBlockNerve,
              onUndoBlockNerve,
              onMarkAsReadyToSeeFamilyPreOp,
              onMarkAsNotReadyToSeeFamilyPreOp,
              onDischargePhysicianLastPatient,
              onDischargeAnesthesiologistLastPatient,
              onDischargeBothLastPatient,
              onTransferToChair,
              onTransferToPT,
              onPTComplete,
              onMoveToOr,
              onMoveToPostOp,
              onAssignBed,
              onDischargePatient,
              onCancelVisit,
              // Time edit handlers
              onTransferToChairTimeSubmit,
              onTransferToPTTimeSubmit,
              onPTCompleteTimeSubmit,
              // Feature flags
              transferToChair: !!mutations.transferToChair,
              transferToPT: !!mutations.transferToPT,
              ptComplete: !!mutations.ptComplete,

              goBack,
              beds: getBeds(filteredPatients, room.capacity),
              // Delay Reason Mutations
              editPatientOobWarningDelayReason: mutations.editPatientOobWarningDelayReason,
              editPatientPtWarningDelayReason: mutations.editPatientPtWarningDelayReason,
              editPatientPtCompleteWarningDelayReason: mutations.editPatientPtCompleteWarningDelayReason,
              editPatientPacuOutWarningDelayReason: mutations.editPatientPacuOutWarningDelayReason,
            }}
            orthopedic={Boolean(screenConfiguration?.orthopedic)}
            pacuV2={Boolean(configuration?.screenVersion === 1)}
            orPacuPatients={patients}
            showDelayReasons={Boolean(screenConfiguration?.showDelayReasons)}
            dischargeModalV2={Boolean(screenConfiguration?.dischargeModalV2)}
          />
        </ScreenConfigurationProvider>
      </Wrap>
    );
  }

  // For overview mode (all rooms of a type), show room type as title instead of specific room name
  const getRoomDisplayName = () => {
    if (filterByRoom && room?.name) {
      // Kiosk mode: show specific room name
      return room.name;
    }
    // Overview mode: show room type as title
    switch (roomType) {
      case PRE_OP:
        return 'PreOp';
      case PACU:
        return 'PACU';
      case POST_OP:
        return 'PostOp';
      default:
        return 'Patients';
    }
  };

  // Render patient list
  return (
    <Fragment>
      {usePACUv2 ? (
        <PACUv2 room={room} patients={filteredPatients} selectPatient={setSelectedPatient} />
      ) : (
        <PatientList
          room={room}
          showReadyForAnesthesia={isPreOp}
          showReadyForSurgeon={isPreOp}
          showSpinalTimeout={isPreOp}
          showHoldProcedure={isPreOp}
          showBlockNerve={isPreOp}
          showBlockNervePacu={isPacu || isPostOp}
          patients={filteredPatients}
          selectPatient={setSelectedPatient}
          roomName={getRoomDisplayName()}
          roomType={roomType}
          hospitalOverviewMode={isHospitalOverviewMode}
          layoutVariant="interactive"
          hasPreOpPriorityModule={hasPreOpPriorityModule}
          hasNoteModule={hasNoteModule}
          hasProcedureNoteModule={hasProcedureNoteModule}
          showLatePreOpIndicator={configuration?.showLatePreOpIndicator}
          hospitalId={scope?.hospital?.id}
        />
      )}
      {filteredPatients && filteredPatients.length === 0 && <ClientUpdater />}
    </Fragment>
  );
}

export default (roomType?: string) => withScope((props: any) => <RoomTabletApp {...props} roomType={roomType} />);
